import RmsClient from '@locuslabs/rms-client';
import { V3 } from '@locuslabs/rms-sdk';
import * as Request from '../request';
import * as Config from '../config';

const rmsClient = async (stage = Config.getStage()) => {
    const accessToken = await Config.getAccessToken();
    return RmsClient(accessToken, stage);
};

const createPoiRequest = Request.makeRequest(rmsClient, 'createPOI');
const getAiJsonRequest = Request.makeRequest(rmsClient, 'getAiJson', Request.parseAiJson);
const getBoundingBoxesRequest = Request.makeRequest(rmsClient, 'getBoundingBoxes');
const getBoundsPolygonsRequest = Request.makeRequest(rmsClient, 'getBoundsPolygons');
const getContentRequest = Request.makeRequest(rmsClient, 'getContent');
const getContentVenueAccountTreeRequest = Request.makeRequest(rmsClient, 'getContentVenueAccountTree');
const getGeoJsonRequest = Request.makeRequest(rmsClient, 'getGeoJson', Request.body);
const listContentRequest = Request.makeRequest(rmsClient, 'listContent');
const listPoisRequest = Request.makeRequest(rmsClient, 'listPOIs');
const openLocationRequest = Request.makeRequest(rmsClient, 'openLocation');
const updatePoisRequest = Request.makeRequest(rmsClient, 'updatePOIs');
const listVenuesForAccountRequest = Request.makeRequest(rmsClient, 'listVenuesForAccount');
const generateSpritesheetRequest = Request.makeRequest(rmsClient, 'generateSpritesheet');
const getVenueDataObjectRequest = Request.makeRequest(rmsClient, 'getVenueDataObject');
const listVenueIdsRequest = Request.makeRequest(rmsClient, 'listVenueIds');
const venueIdIsMigratedRequest = Request.makeRequest(rmsClient, 'venueIdIsMigrated');
const putFeaturesAndEntitiesRequest = Request.makeRequestWithPolicy(
    rmsClient,
    'putFeaturesAndEntities',
    Request.retryWithBreaker,
);
const getFeaturesRequest = Request.makeRequest(rmsClient, 'getFeatures', Request.body);

const getEntitiesRequest = async (venueId, { accountId, format, locale, baseVenueId }) => {
    const stage = Config.getStage();
    const token = await Config.getAccessToken();
    const venueIdForRequest = locale !== 'en' && baseVenueId ? baseVenueId : venueId;

    try {
        const response = await V3({ token, stage }).getEntities(venueIdForRequest, { accountId, format, locale });
        if (response.status === 200) {
            const responseData = Request.axiosData(response);
            const entitiesWithOverrides = applyLocaleOverrides(
                responseData.data.entities,
                responseData.data.localeOverrides,
                venueId,
            );
            return { entities: entitiesWithOverrides };
        }
    } catch (e) {
        Request.failure(e);
    }
};

export const applyLocaleOverrides = (entities, localeOverrides, venueIdOverride) => {
    if (!Array.isArray(entities) || !Array.isArray(localeOverrides)) return entities;

    const overrideFields = ['name', 'description', 'tags', 'displayTags', 'url', 'terminal', 'gate'];

    // Build a lookup map for overrides by entityId
    const overridesById = {};
    for (const override of localeOverrides) {
        if (override.entityId) {
            overridesById[override.entityId] = override;
        }
    }

    return entities.map(entity => {
        const override = overridesById[entity.entityId];
        if (!override) return entity;

        // Only override specified fields if the override value is not null/undefined
        const updatedFields = {};
        for (const field of overrideFields) {
            if (override[field] != null) {
                updatedFields[field] = override[field];
            }
        }

        // Always override venueId if provided
        if (venueIdOverride !== undefined) {
            updatedFields.venueId = venueIdOverride;
        }

        return { ...entity, ...updatedFields };
    });
};

const Rms = {
    createPOI: (id, entity) => createPoiRequest(id, entity),
    getBoundsPolygons: (venueId, opts) => getBoundsPolygonsRequest(venueId, opts),
    getBoundingBoxes: (venueId, opts) => getBoundingBoxesRequest(venueId, opts),
    getGeoJson: (id, levelId) => getGeoJsonRequest(id, levelId),
    getVenue: (venueId, format) => getVenueDataObjectRequest(venueId, Config.VENUE_OBJECT_TYPES.VENUE, { format }),
    generateSpritesheet: (c, venueId, accountId) =>
        generateSpritesheetRequest(c, { venue: venueId, account: accountId }),
    listPOIs: venueId => listPoisRequest(venueId),
    openLocation: (id, obj) => openLocationRequest(id, obj),
    updatePOIs: (venueId, changes) => updatePoisRequest(venueId, changes),
    getAiJson: (venueId, format) => getAiJsonRequest(venueId, format),
    getContentVenueAccountTree: category => getContentVenueAccountTreeRequest(category),
    getVenueStructures: (venueId, format) =>
        getVenueDataObjectRequest(venueId, Config.VENUE_OBJECT_TYPES.STRUCTURE, { format }),
    getVenueLevels: (venueId, format) =>
        getVenueDataObjectRequest(venueId, Config.VENUE_OBJECT_TYPES.LEVEL, { format }),
    getVenueOrdinals: (venueId, format) =>
        getVenueDataObjectRequest(venueId, Config.VENUE_OBJECT_TYPES.ORDINAL, { format }),
    listVenuesForAccount: accountId => listVenuesForAccountRequest(accountId, { locale: 'en' }),
    listTranslatedVenuesForAccount: accountId => listVenuesForAccountRequest(accountId),
    listVenueIds: () => listVenueIdsRequest(),
    venueIdIsMigrated: venueId => venueIdIsMigratedRequest(venueId),
    getFeatures: (venueId, levelId, format = 'geoJson') => getFeaturesRequest(venueId, { levelId, format }),
    getEntities: (venueId, accountId, format = 'legacy', locale, baseVenueId) =>
        getEntitiesRequest(venueId, { accountId, format, locale, baseVenueId }),
    getVenueDataObject: (venueId, objectType, format, locale) =>
        getVenueDataObjectRequest(venueId, objectType, { format, locale }),
    getContent: (contentType, cn, category, venueId, accountId, noExtension) =>
        getContentRequest(category, contentType, cn, { venue: venueId, account: accountId, noExtension }),
    listContent: (contentType, category, venueId, accountId, noExtensions, full) =>
        listContentRequest(category, contentType, { venue: venueId, account: accountId, noExtensions, full }),
    putFeaturesAndEntities: (venueId, payload, options = { format: 'legacy' }) =>
        putFeaturesAndEntitiesRequest(venueId, payload, options),
};

export { Rms };
